import React, { useCallback, useEffect } from "react";
import { authProtectedRoutes, publicRoutes } from "./routes/all-routes";
import { Route, Routes, useNavigate } from "react-router-dom";
import HorizontalLayout from "./layouts/horizontal-layout";
import BlankLayout from "./layouts/blank-layout";
import "./assets/css/globals.css";
import "./assets/scss/custom.scss";
import NonAuthLayout from "./layouts/non-auth-layout";
import { AuthProtected, AuthUnprotected } from "./routes/protected-routes";
import useAppState from "./contexts/state";
import debounce from "lodash.debounce";
import { initAPIInterceptor } from "@services/api/interceptor";
import { AuthState } from "./contexts/slices/auth";

import { HeroUIProvider } from "@heroui/react";
import { BillingProvider } from "./contexts/billing";

import * as amplitude from '@amplitude/analytics-browser';
import { UserData } from "@interfaces/auth";

function App() {
  const { expireSession, user } = useAppState((state: unknown) => ({
    expireSession: (state as AuthState).expireSession,
    user: (state as AuthState).user as UserData,
  }));

  const navigate = useNavigate();

  const debouncedExpireSession = useCallback(debounce(expireSession, 1000), []);

  initAPIInterceptor({ debouncedExpireSession });

  useEffect(() => {
    amplitude.init(import.meta.env.VITE_AMPLITUDE_KEY, {
      userId: user?.id
    });
  }, []);

  // Relay AUTH_SUCCESS from popup to top window if in iframe
  useEffect(() => {
    function handlePopupAuth(event: MessageEvent) {
      if (event.data?.type === 'AUTH_SUCCESS') {
        if (window.parent !== window) {
          window.parent.postMessage(event.data, '*'); // Replace '*' with website origin in production
        }
      }
    }
    window.addEventListener('message', handlePopupAuth);
    return () => window.removeEventListener('message', handlePopupAuth);
  }, []);

  const renderRoute = (route: any, idx: number) => {
    const routeElement = (
      <AuthUnprotected disableRedirect={route.disableRedirect}>
        {route.layout === "blank" ? (
          <BlankLayout>
            {route.component}
          </BlankLayout>
        ) : (
          <NonAuthLayout>
            {route.component}
          </NonAuthLayout>
        )}
      </AuthUnprotected>
    );

    return (
      <Route
        path={route.path}
        key={idx}
        element={routeElement}
      />
    );
  };

  return (
    <React.Fragment>
      <HeroUIProvider navigate={navigate}>
        <Routes>
          {publicRoutes.map((route, idx) => renderRoute(route, idx))}
          {authProtectedRoutes.map((route, idx) => (
            <Route
              path={route.path}
              key={idx}
              element={
                <React.Fragment>
                  <AuthProtected>
                    <BillingProvider>
                      <HorizontalLayout>
                        {route.component}
                      </HorizontalLayout>
                    </BillingProvider>
                  </AuthProtected>
                </React.Fragment>
              }
            />
          ))}
        </Routes>
      </HeroUIProvider>
    </React.Fragment>
  );
}

export default App;
